import { useCallback, useSyncExternalStore } from "react";
import { meshtasticShown } from "../../lib/mapScopes";
import { useAppData } from "../../state/AppData";
import { useGlobalQuery } from "../../api/useGlobalQuery";
import { useZoomedOut } from "./mapZoom";
import type { FmFull, FmMeta } from "../../lib/freqmapperCoverage";

// The FreqMapper coverage layer on the Live Map: whether Settings has it on, the stored coverage, and this viewer's own
// "show on map" switch. The map only ever reads what the backend built and stored; nothing here asks FreqMapper for anything.
// The switch is remembered per viewer in this browser and starts on.
const STORAGE_KEY = "amalgamesh:freqmapperLayer";

function read(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "0";
  } catch {
    return true; // blocked storage: the default
  }
}

let layerOn = read();
const listeners = new Set<() => void>();

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
function snapshot() {
  return layerOn;
}

export interface FreqMapperCoverageState {
  /** Settings has the layer on, so the map shows its switch and legend. */
  available: boolean;
  /** This viewer's switch. */
  layerOn: boolean;
  setLayerOn: (on: boolean) => void;
  /** The description of the stored coverage (or the reason there is none). */
  meta: FmMeta | null;
  /** The stored hexes; null until they have arrived, or when the layer is hidden or has no data. */
  full: FmFull | null;
  /** The map is zoomed out past the point where the layer is drawn (below zoom 9). */
  zoomedOut: boolean;
  /** The protocol filter is MeshCore only, so the layer is off (FreqMapper is Meshtastic only). */
  meshcoreOnly: boolean;
}

export function useFreqMapperCoverage(): FreqMapperCoverageState {
  const on = useSyncExternalStore(subscribe, snapshot, snapshot);
  const zoomedOut = useZoomedOut();
  const { protocolFilter } = useAppData();
  const meshcoreOnly = !meshtasticShown(protocolFilter);
  const setLayerOn = useCallback((next: boolean) => {
    layerOn = next;
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* private mode or blocked storage: it still switches, it just is not remembered */
    }
    listeners.forEach((l) => l());
  }, []);
  // The small description is asked for every minute (it is cheap). The hexes are fetched once per built answer: the query key
  // carries the answer's version, so a new answer is a new key and an unchanged one is never fetched again.
  const metaQ = useGlobalQuery<FmMeta>(["global", "freqmapperMeta"], "/api/freqmapper/coverage?hexes=false", 60_000, true, false);
  const meta = metaQ.data;
  const available = meta?.enabled === true;
  const wantFull = available && meta?.has_data === true && on && !meshcoreOnly;
  const fullQ = useGlobalQuery<FmFull>(["global", "freqmapperFull", meta?.data_version ?? ""], "/api/freqmapper/coverage", 6 * 3600_000, wantFull, false);
  const full = wantFull && fullQ.data && fullQ.data.data_version === meta?.data_version ? fullQ.data : null;
  return { available, layerOn: on, setLayerOn, meta, full, zoomedOut, meshcoreOnly };
}
